import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { BreakEven, leerBreakEven, formatBreakEvenX, formatCompact, formatCurrency } from './calculadoraHelpers';
import { Card, cn, Tone } from '../ui';

ChartJS.register(...registerables);

interface Props {
  be: BreakEven;
  titulo?: string;
}

const toneText: Record<Tone, string> = {
  default: 'text-gappex-primary',
  info: 'text-cyan-600',
  positive: 'text-emerald-600',
  warning: 'text-amber-600',
  negative: 'text-red-500',
};

const toneChip: Record<Tone, string> = {
  default: 'bg-slate-100 text-slate-600',
  info: 'bg-cyan-100 text-cyan-700',
  positive: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  negative: 'bg-red-100 text-red-600',
};

/** Datasets que sólo marcan una posición: no van en el tooltip de valores. */
const AUXILIARES = ['Hoy', 'Equilibrio'];

/** El dataset del borde superior de la banda es, en realidad, el gasto máximo. */
const NOMBRE_TOOLTIP: Record<string, string> = {
  'Rango mín–máx': 'Gasto máx',
};

/** Orden de lectura en el tooltip: ingreso arriba, gastos de mayor a menor. */
const ORDEN_TOOLTIP: Record<string, number> = {
  Ingreso: 0,
  'Rango mín–máx': 1,
  'Gasto prom': 2,
  'Gasto mín': 3,
};

/**
 * Punto de equilibrio como gráfico costo-volumen-utilidad: el ingreso es una
 * recta plana (no depende del uso) y el gasto sube con el uso. Se cruzan en el
 * punto de equilibrio. A la izquierda del cruce hay ganancia; a la derecha,
 * pérdida. El marcador "Hoy" muestra dónde estás parado.
 */
const BreakEvenChart: React.FC<Props> = ({ be, titulo = 'Punto de equilibrio' }) => {
  const lectura = useMemo(() => leerBreakEven(be), [be]);

  const { chartData, options } = useMemo(() => {
    let xMax: number;
    if (be.modo === 'tasa') {
      // El uso no puede pasar del 100%; el eje se queda ahí.
      xMax = 1;
    } else {
      const ref = be.breakEvenX ?? be.currentX;
      xMax = Math.min(Math.max(2, be.currentX * 1.25, ref * 1.25), 6);
    }
    if (!(xMax > 0)) xMax = 1;

    const yTop = Math.max(be.ingresoAnual, be.slopeMax * xMax) * 1.05;

    const cruceVisible =
      be.breakEvenX !== null && be.breakEvenX >= 0 && be.breakEvenX <= xMax;

    // Muestreo compartido del eje x: todas las líneas se dibujan sobre los mismos
    // puntos para que el tooltip (modo 'index') pueda leer los tres escenarios y
    // el ingreso en el x que está bajo el cursor, no sólo en los extremos.
    const pasos = be.modo === 'tasa' ? 100 : 120;
    const paso = xMax / pasos;
    const muestras: number[] = [];
    for (let i = 0; i <= pasos; i++) muestras.push(paso * i);
    // Puntos de interés: que el hover pueda caer exacto sobre hoy y el equilibrio.
    const interes = [be.currentX, be.breakEvenX].filter(
      (x): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0 && x <= xMax
    );
    const puntosX = Array.from(new Set([...muestras, ...interes])).sort((a, b) => a - b);
    const tolerancia = paso / 2;

    /** Recta de gasto: y = pendiente · x, muestreada en todos los puntos. */
    const gasto = (pendiente: number) => puntosX.map((x) => ({ x, y: pendiente * x }));
    /** Recta horizontal (el ingreso no depende del uso). */
    const plano = (y: number) => puntosX.map((x) => ({ x, y }));

    const data = {
      datasets: [
        {
          label: 'Ingreso',
          data: plano(be.ingresoAnual),
          borderColor: '#64748b',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0,
        },
        {
          // Borde inferior de la banda: gasto con consumo mínimo.
          label: 'Gasto mín',
          data: gasto(be.slopeMin),
          borderColor: 'rgba(245,158,11,0.35)',
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0,
        },
        {
          // Borde superior (gasto máximo) + relleno de la banda hacia el mínimo.
          label: 'Rango mín–máx',
          data: gasto(be.slopeMax),
          borderColor: 'rgba(245,158,11,0.35)',
          backgroundColor: 'rgba(245,158,11,0.12)',
          borderWidth: 1,
          pointRadius: 0,
          pointHoverRadius: 3,
          fill: '-1',
          tension: 0,
        },
        {
          // Línea principal: gasto con consumo promedio.
          label: 'Gasto prom',
          data: gasto(be.slope),
          borderColor: '#f59e0b',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0,
        },
        {
          // Marcador vertical "Hoy".
          label: 'Hoy',
          data: [
            { x: be.currentX, y: 0 },
            { x: be.currentX, y: yTop },
          ],
          borderColor: '#2563eb',
          borderWidth: 1.5,
          borderDash: [5, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
        },
        {
          // Punto de equilibrio promedio (donde el gasto prom cruza al ingreso).
          label: 'Equilibrio',
          data: cruceVisible ? [{ x: be.breakEvenX, y: be.ingresoAnual }] : [],
          borderColor: '#ef4444',
          backgroundColor: '#ef4444',
          pointRadius: 5,
          pointHoverRadius: 5,
          showLine: false,
        },
      ],
    };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false, axis: 'x' as const },
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            boxWidth: 12,
            font: { size: 11 },
            // Ocultar de la leyenda los marcadores auxiliares.
            filter: (item: { text: string }) =>
              item.text === 'Ingreso' || item.text === 'Gasto prom' || item.text === 'Rango mín–máx',
          },
        },
        tooltip: {
          // Los marcadores auxiliares no aportan un valor en y: fuera del tooltip.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          filter: (item: any) => !AUXILIARES.includes(item.dataset?.label),
          // De arriba hacia abajo, igual que se ven las líneas en el gráfico.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          itemSort: (a: any, b: any) =>
            (ORDEN_TOOLTIP[a.dataset?.label] ?? 99) - (ORDEN_TOOLTIP[b.dataset?.label] ?? 99),
          callbacks: {
            // Tipos laxos: los TooltipItem de Chart.js traen parsed.x/y como number|null.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            title: (items: any[]) =>
              items.length ? `Uso ${formatBreakEvenX(be.modo, Number(items[0].parsed.x))}` : '',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label: (ctx: any) => {
              const l = ctx.dataset.label || '';
              return `${NOMBRE_TOOLTIP[l] ?? l}: ${formatCurrency(Number(ctx.parsed.y))}`;
            },
            // Resultado con consumo promedio + aviso de hoy / equilibrio.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            footer: (items: any[]) => {
              if (!items.length) return '';
              const x = Number(items[0].parsed.x);
              const resultado = be.ingresoAnual - be.slope * x;
              const lineas = [
                `${resultado >= 0 ? 'Resultado prom' : 'Pérdida prom'}: ${formatCurrency(Math.abs(resultado))}`,
              ];
              const hitos: string[] = [];
              if (Math.abs(x - be.currentX) <= tolerancia) hitos.push('hoy');
              if (be.breakEvenX !== null && Math.abs(x - be.breakEvenX) <= tolerancia) {
                hitos.push('punto de equilibrio');
              }
              if (hitos.length) lineas.push(hitos.join(' · '));
              return lineas;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear' as const,
          min: 0,
          max: xMax,
          title: {
            display: true,
            text: be.modo === 'tasa' ? 'Tasa de uso' : 'Multiplicador del uso actual',
            font: { size: 11 },
            color: '#94a3b8',
          },
          ticks: {
            font: { size: 11 },
            callback: (value: string | number) => formatBreakEvenX(be.modo, Number(value)),
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: {
            font: { size: 11 },
            callback: (value: string | number) => formatCompact(Number(value)),
          },
          grid: { color: '#f1f5f9' },
        },
      },
    };

    return { chartData: data, options: opts };
  }, [be]);

  return (
    <Card className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-gappex-secondary">{titulo}</h2>
          <p className="text-xs text-slate-400 mt-1">
            El ingreso no cambia con el uso; el gasto sí. La banda es el rango entre consumo mínimo y
            máximo de prestaciones; la línea llena es el promedio.
          </p>
        </div>
        <div className="sm:text-right">
          <div className="flex items-center gap-2 sm:justify-end">
            <span className={cn('text-2xl font-bold tabular-nums', toneText[lectura.tono])}>
              {lectura.valor}
            </span>
            <span className={cn('text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5', toneChip[lectura.tono])}>
              {lectura.etiqueta}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            {lectura.hoy}
            {lectura.rango && <span className="text-slate-300"> · rango {lectura.rango}</span>}
          </p>
        </div>
      </div>
      <div className="h-72">
        <Line data={chartData} options={options} />
      </div>
    </Card>
  );
};

export default BreakEvenChart;
